import { useEffect, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { SITE_NAME, startNavigation } from "../analytics";

/**
 * Route pattern → human-readable section name used as the fallback (base)
 * title when a page doesn't publish a richer, data-aware title of its own.
 * The first pattern that matches the current pathname wins, so more specific
 * patterns must precede generic ones. Keep these labels stable: GA4 groups
 * page_view events by `page_title`, so renaming them creates a discontinuity
 * in historical reports.
 */
const ROUTE_TITLES: Array<{ path: string; title: string }> = [
  { path: "/", title: "Próximos eventos" },
  { path: "/leagues", title: "Ligas" },
  { path: "/events/:eventId/decks/:resultId", title: "Decklist" },
  { path: "/events/:id", title: "Evento" },
  { path: "/subarchetypes/:subarchetypeId", title: "Subarquétipo" },
  { path: "/matches", title: "Partidas" },
  { path: "/metagame", title: "Metagame" },
  { path: "/champions", title: "Campeões" },
  { path: "/theadmin/import", title: "Admin · Importar" },
  { path: "/theadmin/leagues", title: "Admin · Ligas" },
  { path: "/theadmin/events", title: "Admin · Eventos" },
  { path: "/theadmin/archetypes", title: "Admin · Arquétipos" },
  { path: "/theadmin/decks/:deckId/decklists", title: "Admin · Decklists" },
  { path: "/theadmin/decks", title: "Admin · Decks" },
  { path: "/theadmin/unclassified", title: "Admin · Sem classificação" },
  { path: "/theadmin/*", title: "Admin" },
];

function sectionForPath(pathname: string): string | null {
  for (const route of ROUTE_TITLES) {
    if (matchPath({ path: route.path, end: true }, pathname)) {
      return route.title;
    }
  }
  return null;
}

function buildBaseTitle(pathname: string): string {
  const section = sectionForPath(pathname);
  return section ? `${section} · ${SITE_NAME}` : SITE_NAME;
}

/**
 * Mount once inside the Router. On every SPA route change, registers a new
 * navigation with the analytics module: sets `document.title` to the base
 * title immediately for browser tab UX and schedules a deferred GA4
 * `page_view`. Dynamic pages can call `usePageTitle` (or `setNavigationTitle`
 * directly) to publish a richer title before the buffer flushes. Renders
 * nothing.
 */
export function AnalyticsRouteTracker(): null {
  const location = useLocation();
  const baseTitle = useMemo(
    () => buildBaseTitle(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    const path = location.pathname + location.search;
    startNavigation(path, baseTitle);
  }, [location.pathname, location.search, baseTitle]);

  return null;
}
